import * as React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@bi-studio/shared/hooks";
import { AppLayout, LoadingScreen, ErrorState, ConfirmationModal } from "@bi-studio/shared/components";
import { useForm } from "@mantine/form";
import {
    createTabGroup,
    addFolderItem,
} from "@bi-studio/shared/apis";
import type { TabGroupInput } from "@bi-studio/shared/lib";
import {
    PageContainer,
    PageActions,
    TabGroupCreateActionsButton
} from "@/components";
import { TabGroupCreateView } from "@/components/tab-groups/TabGroupCreateView";
import { useAppDispatch, useAppSelector } from "@/store/hooks";
import {
    fetchTabGroupSelectionLists,
    selectTabGroupReports,
    selectTabGroupCharts,
    selectTabGroupDashboards,
    selectTabGroupForms,
    selectTabGroupListsLoading,
    selectTabGroupError,
    setTabGroupError,
    resetTabGroupState
} from "@/store/slices/tabGroupSlice";
import {
    setTabGroupDraft,
    selectTabGroupDraft
} from "@/store/slices/draftSlice";

export default function CreateTabGroupPage() {
    const { isAuthenticated, isLoading: authLoading, token } = useAuth();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const [searchParams] = useSearchParams();
    const folderId = searchParams.get("folderId");
    const tabGroupDraft = useAppSelector(selectTabGroupDraft);

    const [activeTab, setActiveTab] = React.useState<string | null>("general");
    const [isSaving, setIsSaving] = React.useState(false);
    
    const reports = useAppSelector(selectTabGroupReports);
    const charts = useAppSelector(selectTabGroupCharts);
    const dashboards = useAppSelector(selectTabGroupDashboards);
    const forms = useAppSelector(selectTabGroupForms);
    const loadingData = useAppSelector(selectTabGroupListsLoading);
    const error = useAppSelector(selectTabGroupError);

    const form = useForm<TabGroupInput>({
        initialValues: {
            name: "",
            description: "",
            allowedRoles: [],
            tabs: [],
        },
        validate: {
            name: (value) => (value ? null : "Name is required"),
        },
    });

    const [isRestoreModalOpen, setIsRestoreModalOpen] = React.useState(false);

    // Handle draft restoration
    React.useEffect(() => {
        if (tabGroupDraft && form.values.name === "") {
            setIsRestoreModalOpen(true);
        }
    }, []);

    const handleRestoreDraft = () => {
        form.setValues(structuredClone(tabGroupDraft) as TabGroupInput);
        setIsRestoreModalOpen(false);
    };

    const handleDiscardDraft = () => {
        dispatch(setTabGroupDraft(null));
        setIsRestoreModalOpen(false);
    };

    // Auto-save draft
    React.useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (form.values.name || form.values.tabs.length > 0) {
                dispatch(setTabGroupDraft(structuredClone(form.values)));
            }
        }, 2000);
        return () => clearTimeout(timeoutId);
    }, [form.values, dispatch]);

    React.useEffect(() => {
        if (token) {
            dispatch(fetchTabGroupSelectionLists(token));
        }

        return () => {
            dispatch(resetTabGroupState());
        };
    }, [dispatch, token]);

    const handleLogout = () => {
        navigate("/login");
    };

    if (authLoading) {
        return (
            <AppLayout onLogout={handleLogout} title="Workspace" subtitle="BI Studio">
                <LoadingScreen message="Initializing workspace..." />
            </AppLayout>
        );
    }

    if (!isAuthenticated) {
        return null;
    }

    const handleSave = async () => {
        const validation = form.validate();
        if (validation.hasErrors) return;

        if (!token) return;

        for (const tab of form.values.tabs) {
            if (tab.type === "report" && !tab.reportId) {
                dispatch(setTabGroupError("Please select a report for all tabs"));
                setActiveTab("tabs");
                return;
            }
            if (tab.type === "chart" && !tab.chartId) {
                dispatch(setTabGroupError("Please select a chart for all tabs"));
                setActiveTab("tabs");
                return;
            }
            if (tab.type === "dashboard" && !tab.dashboardId) {
                dispatch(setTabGroupError("Please select a dashboard for all tabs"));
                setActiveTab("tabs");
                return;
            }
            if (tab.type === "form" && !tab.formId) {
                dispatch(setTabGroupError("Please select a form for all tabs"));
                setActiveTab("tabs");
                return;
            }
        }

        try {
            setIsSaving(true);
            dispatch(setTabGroupError(null));

            const created = await createTabGroup(token, form.values);
            dispatch(setTabGroupDraft(null)); // Clear draft on success

            if (folderId && folderId !== "root") {
                try {
                    await addFolderItem(token, folderId, {
                        itemType: "tab_group",
                        itemId: created.id,
                    });
                } catch (err) {
                    console.error("Failed to add tab group to folder:", err);
                }
            }

            if (folderId && folderId !== "root") {
                navigate(`/folders/${folderId}`);
            } else {
                navigate("/folders");
            }
        } catch (err) {
            dispatch(setTabGroupError(err instanceof Error ? err.message : "Failed to create tab group"));
            setIsSaving(false);
        }
    };

    const handleCancel = () => {
        if (folderId && folderId !== "root") {
            navigate(`/folders/${folderId}`);
        } else {
            navigate("/folders");
        }
    };

    const handleBack = handleCancel;

    return (
        <AppLayout
            title="Workspace"
            subtitle="BI Studio"
            onLogout={handleLogout}
        >
            <PageContainer>
                <PageActions 
                    title="Create Tab Group" 
                    onBack={handleBack}
                >
                    <TabGroupCreateActionsButton
                        handleCancel={handleCancel}
                        handleSave={handleSave}
                        isSaving={isSaving}
                    />
                </PageActions>

                {error && (
                    <ErrorState 
                        title="Failed to create tab group"
                        message={error || undefined}
                        onAction={() => dispatch(setTabGroupError(null))}
                    />
                )}

                <TabGroupCreateView
                    isSaving={isSaving}
                    loadingData={loadingData}
                    activeTab={activeTab}
                    setActiveTab={setActiveTab}
                    form={form}
                    reports={reports}
                    charts={charts}
                    dashboards={dashboards}
                    forms={forms}
                />

                <ConfirmationModal
                    opened={isRestoreModalOpen}
                    onClose={handleDiscardDraft}
                    onConfirm={handleRestoreDraft}
                    title="Unsaved Draft Found"
                    confirmLabel="Restore"
                    cancelLabel="Discard"
                >
                    You have an unsaved tab group draft. Would you like to restore it?
                </ConfirmationModal>
            </PageContainer>
        </AppLayout>
    );
}
